import styles from '../index.module.scss';
import Typography from '@primitives/Typography/Typography';
import Button from '@primitives/Button/Button';
import UploadFileIcon from 'src/assets/icons/UploadFileIcon';
import { CheckIcon, CloseIcon } from '@navi/web-ui/lib/icons';
import cx from 'classnames';
import { RootState } from '@cp/src/store';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { ErrMessages, UploadFileResponse } from '../../types';
import {
  downloadSampleFile,
  uploadAgencyAllocationFile,
  uploadAllocationFile
} from '../../allocationServicesAction';
import { ICaseAllocationFileUploadContainer } from '../interfaces';

const CaseAllocationFileUploadContainer = (props: ICaseAllocationFileUploadContainer) => {
  const {
    typeOfAllocation,
    isAllocatingForAgency,
    uploadedFile,
    hasError,
    setUploadedFile,
    setHasError,
    setMessage,
    setSuccessToast
  } = props;
  const [isDragging, setIsDragging] = useState(false);
  const user = useSelector((state: RootState) => state.common.userData);
  const dispatch = useDispatch();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE_IN_MB = 5;
  const MAX_FILE_SIZE = 1e6 * MAX_FILE_SIZE_IN_MB;
  const validationOfFile = (file: File | undefined) => {
    if (file) {
      if (file?.size > MAX_FILE_SIZE) {
        return false;
      }
    }
    return true;
  };
  const handleDownloadClick = () => {
    dispatch(downloadSampleFile('ALLOCATION'));
  };
  const handleUploadClick = () => {
    hiddenFileInput.current?.focus();
    hiddenFileInput.current?.click();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    handleFile(selectedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };
  const handleFile = (file: File | undefined) => {
    if (file) {
      setUploadedFile(file);
      if (!validationOfFile(file)) {
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_AGENCY_ALLOCATION_FILE_SIZE_FAILURE, {
          fileSize: file?.size,
          fileName: file?.name
        });
        setHasError(true);
        setTimeout(() => {
          setUploadedFile(null);
          if (hiddenFileInput && hiddenFileInput?.current) {
            hiddenFileInput.current.value = '';
          }
          setHasError(false);
        }, 2000);
      } else {
        setHasError(false);
      }
    }
  };
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const selectedFile = event.dataTransfer.files?.[0];
    handleFile(selectedFile);
  };
  const handleCancel = () => {
    setUploadedFile(null);
    if (hiddenFileInput && hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }
    setHasError(false);
  };

  const processError = (response: UploadFileResponse | null) => {
    if (!response) {
      return;
    }
    setUploadedFile(null);
    if (hiddenFileInput && hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }
    if (response?.message) {
      setMessage({ success: false, error: true, message: ErrMessages.UPLOAD });
    }
  };

  const handleButtonClick = async () => {
    let response;
    if (uploadedFile) {
      if (isAllocatingForAgency) {
        response = await uploadAgencyAllocationFile(uploadedFile, typeOfAllocation.toLowerCase());
      } else {
        response = await uploadAllocationFile(
          uploadedFile,
          user?.agencyCode || '',
          typeOfAllocation.toLowerCase()
        );
      }
      if (response?.success) {
        setUploadedFile(null);
        if (hiddenFileInput && hiddenFileInput?.current) {
          hiddenFileInput.current.value = '';
        }
        setMessage({ success: true, error: false, message: '' });
        setSuccessToast();
      } else {
        processError(response);
      }
      setTimeout(() => {
        setMessage({ error: false, success: false, message: '' });
      }, 5000);
    }
  };

  return (
    <div className={styles.MiddleContainer}>
      <div className={styles.card}>
        <div className={styles.headerContainer}>
          <Typography variant={'h3'}>Select a file</Typography>
        </div>

        <div
          className={cx({
            [styles.subContainer]: true,
            [styles.dragover]: isDragging && !uploadedFile
          })}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <UploadFileIcon />
          <input
            id="fileUpload"
            type="file"
            accept=".csv, .xls, .xlsx"
            onChange={handleChange}
            ref={hiddenFileInput}
            style={{ display: 'none' }}
          />
          <div className={styles.textContainer}>
            <Button variant="text" onClick={handleUploadClick}>
              Click to Select
            </Button>{' '}
            or drag and drop here
          </div>

          <div className={styles.text1Wrapper}>
            <Typography variant={'p5'} className={styles.text1}>
              Accepted file type .csv and .xls
            </Typography>
          </div>
          <Button onClick={handleDownloadClick} variant="text" className={styles.text2}>
            {' '}
            Download Sample file
          </Button>
        </div>

        {uploadedFile && !hasError ? (
          <div className={styles.uploadBanner}>
            <CheckIcon color="var(--green-base)" fill="var(--green-base)" />
            <span className={styles.fileName}>{uploadedFile?.name}</span>
            <Button variant="text" onClick={handleCancel}>
              <CloseIcon />
            </Button>
          </div>
        ) : null}

        <div className={styles.BigButton}>
          <Button disabled={!uploadedFile || hasError} onClick={handleButtonClick}>
            {typeOfAllocation}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CaseAllocationFileUploadContainer;
